import { z } from 'zod';

type NameMessages = {
  tooShort?: string;
  tooLong?: string;
  required?: string;
};

interface BuildNameOptions {
  min?: number;
  max?: number;
  required?: boolean;
  allowEmptyLiteral?: boolean;
  trim?: boolean;
  messages?: NameMessages;
}

function buildNameSchema(options?: BuildNameOptions & { required?: true }): z.ZodString;
function buildNameSchema(
  options: BuildNameOptions & { required: false },
): z.ZodOptional<z.ZodString>;
function buildNameSchema(options?: BuildNameOptions) {
  const {
    min = 1,
    max = 50,
    required = true,
    allowEmptyLiteral = false,
    trim = true,
    messages = {},
  } = options ?? {};
  const base: z.ZodString = trim ? z.string().trim() : z.string();
  if (required) {
    const withMin =
      min <= 1 && messages.required
        ? base.min(1, { message: messages.required ?? 'errors.firstNameRequired' })
        : base.min(min, { message: messages.tooShort ?? 'errors.firstNameTooShort' });
    return withMin.max(max, { message: messages.tooLong ?? 'errors.firstNameTooLong' });
  }
  const optional = base.optional();
  return optional.superRefine((value, ctx) => {
    if (value === undefined) return;
    const len = value.length;
    if (len === 0) {
      if (allowEmptyLiteral) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.tooShort ?? 'errors.firstNameTooShort',
      });
      return;
    }
    if (len < min) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.tooShort ?? 'errors.firstNameTooShort',
      });
    }
    if (len > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: messages.tooLong ?? 'errors.firstNameTooLong',
      });
    }
  });
}

interface BuildPasswordFieldOptions {
  requiredMessage: string;
}

function buildRequiredPasswordField({ requiredMessage }: BuildPasswordFieldOptions) {
  return z.string().nonempty(requiredMessage);
}

interface BuildEmailOptions {
  required?: boolean;
  trim?: boolean;
  requiredMessage?: string;
  invalidMessage?: string;
}

function buildEmailSchema(options?: BuildEmailOptions & { required?: true }): z.ZodString;
function buildEmailSchema(
  options: BuildEmailOptions & { required: false },
): z.ZodOptional<z.ZodString>;
function buildEmailSchema({
  required = true,
  trim = true,
  requiredMessage = 'errors.emailRequired',
  invalidMessage = 'errors.invalidEmail',
}: BuildEmailOptions = {}) {
  let base = z.string();
  if (trim) base = base.trim();
  const withStrictValidation = (schema: z.ZodString) =>
    schema.regex(z.regexes.html5Email, invalidMessage).regex(z.regexes.email, invalidMessage);
  if (required) {
    return withStrictValidation(base.nonempty(requiredMessage));
  }
  return withStrictValidation(base).optional();
}

interface BuildPhoneOptions {
  required?: boolean;
  nullable?: boolean;
  trim?: boolean;
  invalidMessage?: string;
  allowEmptyString?: boolean;
}

function buildPhoneSchema(
  options?: BuildPhoneOptions & { required?: false; nullable?: false },
): z.ZodOptional<z.ZodString>;
function buildPhoneSchema(options: BuildPhoneOptions & { required: true }): z.ZodString;
function buildPhoneSchema(
  options: BuildPhoneOptions & { required?: false; nullable: true },
): z.ZodNullable<z.ZodOptional<z.ZodString>>;
function buildPhoneSchema({
  required = false,
  nullable = false,
  trim = true,
  invalidMessage = 'errors.invalidPhone',
  allowEmptyString = true,
}: BuildPhoneOptions = {}) {
  const base: z.ZodString = trim ? z.string().trim() : z.string();
  const shaped = required ? base : nullable ? base.optional().nullable() : base.optional();

  // Strengthened phone validation: allow digits and common separators, + only at start, 7–15 digits
  return shaped.superRefine((value, ctx) => {
    if (value === undefined || value === null) return; // handled by optional/nullable
    if (value.length === 0 && allowEmptyString) return; // empty is allowed when optional
    if (value.length === 0 && !allowEmptyString) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: invalidMessage });
      return;
    }
    const validChars = /^(?:\+)?[0-9().\-\s]+$/.test(value);
    const digitCount = (value.match(/\d/g) ?? []).length;
    const plusCount = (value.match(/\+/g) ?? []).length;
    const plusPositionInvalid = plusCount === 1 && !value.startsWith('+');
    if (!validChars || plusCount > 1 || plusPositionInvalid || digitCount < 7 || digitCount > 15) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: invalidMessage });
    }
  });
}

interface BuildAddressLineOptions {
  required?: boolean;
  trim?: boolean;
  requiredMessage?: string;
}

function buildAddress1Schema(options?: BuildAddressLineOptions & { required?: true }): z.ZodString;
function buildAddress1Schema(
  options: BuildAddressLineOptions & { required: false },
): z.ZodOptional<z.ZodString>;
function buildAddress1Schema({
  required = true,
  trim = true,
  requiredMessage = 'errors.address1Required',
}: BuildAddressLineOptions = {}) {
  let base = z.string();
  if (trim) base = base.trim();
  return required ? base.min(1, { message: requiredMessage }) : base.optional();
}

function buildAddress2Schema(options?: {
  trim?: boolean;
}): z.ZodNullable<z.ZodOptional<z.ZodString>>;
function buildAddress2Schema({ trim = true }: { trim?: boolean } = {}) {
  let base = z.string();
  if (trim) base = base.trim();
  return base.optional().nullable();
}

function buildCitySchema(options?: BuildAddressLineOptions & { required?: true }): z.ZodString;
function buildCitySchema(
  options: BuildAddressLineOptions & { required: false },
): z.ZodOptional<z.ZodString>;
function buildCitySchema({
  required = true,
  trim = true,
  requiredMessage = 'errors.cityRequired',
}: BuildAddressLineOptions = {}) {
  let base = z.string();
  if (trim) base = base.trim();
  return required ? base.min(1, { message: requiredMessage }) : base.optional();
}

interface BuildStateOrProvinceOptions {
  required?: boolean;
  trim?: boolean;
  requiredMessage?: string;
}

function buildStateOrProvinceSchema(
  options?: BuildStateOrProvinceOptions & { required?: false },
): z.ZodNullable<z.ZodOptional<z.ZodString>>;
function buildStateOrProvinceSchema(
  options: BuildStateOrProvinceOptions & { required: true },
): z.ZodString;
function buildStateOrProvinceSchema({
  required = false,
  trim = true,
  requiredMessage = 'errors.stateRequired',
}: BuildStateOrProvinceOptions = {}) {
  let base = z.string();
  if (trim) base = base.trim();
  if (required) return base.min(1, { message: requiredMessage });
  return base.optional().nullable();
}

interface BuildPostalCodeOptions {
  required?: boolean;
  trim?: boolean;
  requiredMessage?: string;
}

function buildPostalCodeSchema(options?: BuildPostalCodeOptions & { required?: true }): z.ZodString;
function buildPostalCodeSchema(
  options: BuildPostalCodeOptions & { required: false },
): z.ZodOptional<z.ZodString>;
function buildPostalCodeSchema({
  required = true,
  trim = true,
  requiredMessage = 'errors.postalCodeRequired',
}: BuildPostalCodeOptions = {}) {
  let base = z.string();
  if (trim) base = base.trim();
  return required ? base.min(1, { message: requiredMessage }) : base.optional();
}

interface BuildCountryCodeOptions {
  required?: boolean;
  requiredMessage?: string;
}

function buildCountryCodeSchema(
  options?: BuildCountryCodeOptions & { required?: true },
): z.ZodPipe<z.ZodString, z.ZodTransform<string, string>>;
function buildCountryCodeSchema(
  options: BuildCountryCodeOptions & { required: false },
): z.ZodOptional<z.ZodPipe<z.ZodString, z.ZodTransform<string, string>>>;
function buildCountryCodeSchema({
  required = true,
  requiredMessage = 'errors.countryRequired',
}: BuildCountryCodeOptions = {}) {
  const base = z
    .string()
    .min(2, { message: requiredMessage })
    .max(2, { message: requiredMessage })
    .transform((v) => v.toUpperCase());
  return required ? base : base.optional();
}

export const accountFieldSchemas = {
  /** Build a first name schema with configurable requirements and messages */
  buildFirstName: buildNameSchema,
  /** Build a last name schema with configurable requirements and messages */
  buildLastName: buildNameSchema,
  /** Build an email schema with configurable requirements and messages */
  buildEmail: buildEmailSchema,
  /** Build a phone schema using the shared strengthened rules */
  buildPhone: buildPhoneSchema,
  /** Build address line 1 schema (required by default) */
  buildAddress1: buildAddress1Schema,
  /** Build address line 2 schema (optional, nullable) */
  buildAddress2: buildAddress2Schema,
  /** Build city schema (required by default) */
  buildCity: buildCitySchema,
  /** Build state/province schema (optional+nullable by default) */
  buildStateOrProvince: buildStateOrProvinceSchema,
  /** Build postal code schema (required by default) */
  buildPostalCode: buildPostalCodeSchema,
  /** Build country code schema (required, 2 letters, uppercased) */
  buildCountryCode: buildCountryCodeSchema,
  /** Build a required password field with provided error message */
  buildRequiredPasswordField: buildRequiredPasswordField,
};
